import { AnswerBlock, AnswerPhoto, AnswerPhotoKind, Message, MessageDraft, MessageOutcome, ReceptionContactInfo } from '../models/message';
import { CONFLICTING_CLIENT_TURN_TITLE, ConciergeGetChatResponse, ConciergeMessageDraftResponse, ConciergeMessageResponse } from '../models/conciergeContracts';

/**
 * All knowledge about the wire shape of Concierge messages lives in this file.
 * If the server renames a field, fix it here and nowhere else.
 */

const OUTCOMES: MessageOutcome[] = ['Answered', 'Abstained', 'Rejected', 'ProviderError'];

/** Chat history as consumed by the UI (server list plus conversation-level hints). */
export interface ChatHistory {
   messages: Message[];
   preferredLanguage?: string;
   version?: number;
   /** The hotel's name for the header, when the API sends one. */
   hotelName?: string;
   /** Example questions for the empty state (at most three are shown), when the API sends them. */
   suggestedQuestions?: string[];
}

function firstString(...candidates: unknown[]): string | undefined {
   for (const c of candidates) {
      if (typeof c === 'string' && c.trim() !== '') return c;
   }
   return undefined;
}

function parseDate(value: unknown): Date | undefined {
   if (!value) return undefined;
   const d = value instanceof Date ? value : new Date(value as string);
   // .NET default(DateTime) serialises as 0001-01-01 — treat as "no date".
   if (isNaN(d.getTime()) || d.getFullYear() <= 1900) return undefined;
   return d;
}

export function formatMessageTime(date: Date): string {
   return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function normalizeStringList(value: unknown): string[] {
   if (!Array.isArray(value)) return [];
   return value.filter((v): v is string => typeof v === 'string').map((v) => v.trim()).filter((v) => v !== '');
}

export function mapOutcome(value: unknown): MessageOutcome | undefined {
   if (typeof value !== 'string') return undefined;
   const lowered = value.trim().toLowerCase();
   return OUTCOMES.find((o) => o.toLowerCase() === lowered);
}

export function mapReceptionContact(raw: unknown): ReceptionContactInfo | undefined {
   if (!raw || typeof raw !== 'object') return undefined;
   const r = raw as Record<string, unknown>;
   const phones = normalizeStringList(r.phones ?? r.phoneNumbers ?? r.phone);
   const emails = normalizeStringList(r.emails ?? r.emailAddresses ?? r.email);
   return { phones, emails };
}

/** Small deterministic string hash (djb2) used to derive stable ids for id-less legacy messages. */
/**
 * Answer blocks from the server. Unknown kinds become text, empty blocks are dropped, and a value is kept only on an
 * item whose text contains it (the component bolds exactly that span).
 */
export function mapAnswerBlocks(raw: unknown): AnswerBlock[] {
   if (!Array.isArray(raw)) return [];
   const blocks: AnswerBlock[] = [];
   for (const entry of raw) {
      const text = firstString(entry?.text)?.trim();
      if (!text) continue;
      const kind = firstString(entry?.kind)?.toLowerCase();
      const block: AnswerBlock = { kind: kind === 'heading' || kind === 'item' ? kind : 'text', text };
      const value = firstString(entry?.value)?.trim();
      if (block.kind === 'item' && value && text.includes(value)) block.value = value;
      blocks.push(block);
   }
   return blocks;
}

const PHOTO_KINDS: readonly AnswerPhotoKind[] = ['restaurant', 'dish', 'amenity', 'room'];
/** The most photos one answer shows (the server sends at most 24, of at most 12 places). */
export const MAX_ANSWER_PHOTOS = 24;

const positiveSize = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : undefined);

/**
 * Photos beside an answer. Only absolute https addresses are kept (anything else is never loaded), each needs the
 * place's name, the alt text falls back to that name, a caption that only repeats the name is left out, a size is
 * kept only when both sides are known, and the place and the block that names it are kept when the server says.
 */
export function mapAnswerPhotos(raw: unknown): AnswerPhoto[] {
   if (!Array.isArray(raw)) return [];
   const photos: AnswerPhoto[] = [];
   for (const entry of raw) {
      const address = firstString(entry?.url)?.trim();
      const title = firstString(entry?.title)?.trim();
      if (!address || !title) continue;
      let url: URL;
      try {
         url = new URL(address);
      } catch {
         continue;
      }
      if (url.protocol !== 'https:') continue;
      const kind = firstString(entry?.kind)?.toLowerCase() as AnswerPhotoKind | undefined;
      const photo: AnswerPhoto = {
         kind: kind && PHOTO_KINDS.includes(kind) ? kind : 'other',
         title,
         url: url.href,
         alt: firstString(entry?.altText)?.trim() || title
      };
      const caption = firstString(entry?.caption)?.trim();
      if (caption && caption.toLocaleLowerCase() !== title.toLocaleLowerCase()) photo.caption = caption;
      const width = positiveSize(entry?.width);
      const height = positiveSize(entry?.height);
      if (width && height) {
         photo.width = width;
         photo.height = height;
      }
      const place = firstString(entry?.place)?.trim();
      if (place) photo.place = place;
      if (Number.isInteger(entry?.block) && entry.block >= 0) photo.block = entry.block;
      photos.push(photo);
      if (photos.length === MAX_ANSWER_PHOTOS) break;
   }
   return photos;
}

function hashText(text: string): string {
   let h = 5381;
   for (let i = 0; i < text.length; i++) {
      h = ((h << 5) + h + text.charCodeAt(i)) | 0;
   }
   return (h >>> 0).toString(36);
}

/**
 * Maps a server message (SignalR or history) to the UI model.
 *
 * `clientTurnId` (the client's idempotency key) and `turnId` (the server turn
 * id) are read separately; they are different values.
 *
 * Identity, in order of preference:
 * 1. the server `id`;
 * 2. `sender + turn + createdAt + text hash` — identical for the SignalR and
 *    history copies (a turn can have several assistant messages, so the turn
 *    alone is not enough);
 * 3. `user + turn` for a guest message without a date (one per turn);
 * 4. a random id, flagged `isOptimistic` so reconciliation can still replace it.
 */
export function mapApiMessageToMessage(apiMessage: ConciergeMessageResponse): Message {
   const raw = apiMessage as ConciergeMessageResponse & Record<string, unknown>;
   const senderRaw = typeof raw.sender === 'string' ? raw.sender.toLowerCase() : '';
   const sender = senderRaw === 'agent' || senderRaw === 'bot' || senderRaw === 'assistant' ? 'bot' : 'user';
   const created = parseDate(raw.createdAt);
   const text = firstString(raw.message, raw.text, raw.content) ?? '';
   const clientTurnId = firstString(raw.clientTurnId);
   const turnId = firstString(raw.turnId);
   const turnKey = clientTurnId ?? turnId;
   const serverId = firstString(raw.id, raw.messageId);

   let id: string;
   let unstableIdentity = false;
   if (serverId) {
      id = serverId;
   } else if (created) {
      id = `${sender}-${turnKey ?? 'noturn'}-${created.getTime()}-${hashText(text)}`;
   } else if (sender === 'user' && turnKey) {
      id = `user-turn-${turnKey}`;
   } else {
      id = `tmp-${sender}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      unstableIdentity = true;
   }

   const outcome = sender === 'bot' ? mapOutcome(raw.outcome) : undefined;
   const sources = sender === 'bot' ? normalizeStringList(raw.sources) : [];
   const receptionContact = sender === 'bot' ? mapReceptionContact(raw.receptionContact) : undefined;
   const blocks = sender === 'bot' ? mapAnswerBlocks(raw.blocks) : [];
   const photos = sender === 'bot' ? mapAnswerPhotos(raw.photos) : [];

   const message: Message = {
      id,
      text,
      sender,
      timestamp: formatMessageTime(created ?? new Date()),
      createdAt: created?.toISOString()
   };

   if (clientTurnId) message.clientTurnId = clientTurnId;
   if (turnId) message.turnId = turnId;
   const language = firstString(raw.language);
   if (language) message.language = language;
   if (unstableIdentity) message.isOptimistic = true;
   if (outcome) message.outcome = outcome;
   if (sources.length > 0) message.sources = sources;
   if (receptionContact) message.receptionContact = receptionContact;
   if (blocks.length > 0) message.blocks = blocks;
   if (photos.length > 0) message.photos = photos;
   if (typeof raw.suggestReception === 'boolean') message.suggestReception = raw.suggestReception;

   return message;
}

/**
 * Maps a `ReceiveMessageDraft` payload: the answer so far for a turn. A draft is keyed by its turn (`clientTurnId`,
 * else `turnId`), never by an id derived from its text, so the next draft of the turn replaces it; it carries no
 * outcome, sources, photos or reception contact. Returns undefined when it names no turn.
 */
export function mapApiDraftToDraft(apiDraft: ConciergeMessageDraftResponse): MessageDraft | undefined {
   const raw = (apiDraft ?? {}) as ConciergeMessageDraftResponse & Record<string, unknown>;
   const clientTurnId = firstString(raw.clientTurnId);
   const turnId = firstString(raw.turnId);
   const turnKey = clientTurnId ?? turnId;
   if (!turnKey) return undefined;

   const draft: MessageDraft = { turnKey, text: firstString(raw.message, raw.text) ?? '', blocks: mapAnswerBlocks(raw.blocks) };
   if (clientTurnId) draft.clientTurnId = clientTurnId;
   if (turnId) draft.turnId = turnId;
   const language = firstString(raw.language);
   if (language) draft.language = language;
   return draft;
}

/**
 * Maps a `GET /api/chat` response to the list of UI messages.
 */
export function mapApiChatToMessages(chatResponse: ConciergeGetChatResponse): Message[] {
   if (!chatResponse?.messages || chatResponse.messages.length === 0) {
      return [];
   }
   return chatResponse.messages.map((msg) => mapApiMessageToMessage(msg));
}

/**
 * Maps a `GET /api/chat` response to messages plus conversation-level hints.
 */
export function mapApiChatToHistory(chatResponse: ConciergeGetChatResponse): ChatHistory {
   const raw = (chatResponse ?? {}) as ConciergeGetChatResponse & Record<string, unknown>;
   const hotel = raw.hotel && typeof raw.hotel === 'object' ? (raw.hotel as Record<string, unknown>) : undefined;
   const hotelName = firstString(raw.hotelName, hotel?.name, hotel?.displayName)?.trim();
   const suggestedQuestions = normalizeStringList(raw.suggestedQuestions ?? raw.suggestions).slice(0, 3);
   const history: ChatHistory = {
      messages: mapApiChatToMessages(chatResponse),
      preferredLanguage: firstString(chatResponse?.preferredLanguage),
      version: typeof chatResponse?.version === 'number' ? chatResponse.version : undefined
   };
   if (hotelName) history.hotelName = hotelName;
   if (suggestedQuestions.length > 0) history.suggestedQuestions = suggestedQuestions;
   return history;
}

/** Minimal structural view of the API `ErrorResponse` (see `file.ts`). */
interface ErrorLike {
   type?: string;
   title?: string;
   details?: { [key: string]: any };
}

/**
 * True when the server rejected a send because the same `clientTurnId` was
 * already used with different text. The title is the documented signal; the
 * type/code checks only absorb naming drift.
 */
export function isConflictingClientTurnError(error?: ErrorLike | null): boolean {
   if (!error) return false;
   const code = CONFLICTING_CLIENT_TURN_TITLE.toLowerCase();
   const candidates = [error.title, error.type, error.details?.code, error.details?.errorCode];
   return candidates.some((c) => typeof c === 'string' && c.trim().toLowerCase() === code);
}
